import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiShare2,
  FiMapPin,
  FiPhone,
  FiMail,
  FiUser,
  FiArrowLeftCircle,
} from "react-icons/fi";
import { TfiLocationPin } from "react-icons/tfi";
import { CiClock2 } from "react-icons/ci";
import {
  FaBriefcase,
  FaGraduationCap,
  FaUsers,
  FaUserTie,
  FaMoon,
  FaHome,
} from "react-icons/fa";
import {
  MdFitnessCenter,
  MdOutlineHeight,
  MdFavorite,
  MdOutlineZoomOutMap,
  MdZoomOutMap,
  MdEmail,
} from "react-icons/md";
import {
  IoCashOutline,
  IoFemale,
  IoMale,
  IoSchoolOutline,
  IoBriefcaseOutline,
  IoHomeOutline,
  IoPeopleOutline,
  IoPerson,
  IoCalendarOutline,
  IoLocationSharp,
  IoImages,
  IoHeartOutline,
} from "react-icons/io5";
import { IoMdContact, IoIosHeartEmpty } from "react-icons/io";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import "./DetailsPage.css";
import { useDashboardContext } from "../../context/Dashboard_context";
import { useLocation } from "react-router-dom";
import Notification from "../../utils/Notification";
import { getAgeFromDob } from "../../utils/helper";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";

const InfoRow = ({ icon, label, value }) => (
  <div className="dp-info-row">
    <div className="dp-info-icon">{icon}</div>
    <div className="dp-info-content">
      <div className="dp-info-label">{label}</div>
      <div className="dp-info-value">{value || "N/A"}</div>
    </div>
  </div>
);

const SectionCard = ({ iconClass, icon, title, children }) => (
  <div className="dp-section">
    <div className="dp-section-header">
      <div className={`dp-section-icon ${iconClass}`}>{icon}</div>
      <span className="dp-section-title">{title}</span>
    </div>
    {children}
  </div>
);

const DetailsPage = () => {
  const navigate = useNavigate();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [isAvatarLightbox, setIsAvatarLightbox] = useState(false);
  const [swiperRef, setSwiperRef] = useState(null);

  const location = useLocation();
  const { selected_profile } = useDashboardContext();

  const openLightbox = (idx) => {
    setActiveIdx(idx);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handlePop = (e) => {
      e.preventDefault();
      setLightboxOpen(false);
      window.history.pushState(null, "");
    };
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, [lightboxOpen]);

  const p =
    location.state?.profile ||
    selected_profile ||
    JSON.parse(localStorage.getItem("selected_profile"));

  const handleShare = async () => {
    const shareData = {
      title: `${p.name} Profile`,
      text: `Check out the profile of ${p.name} on our app!`,
      url: window.location.href,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        Notification("success", "Link copied to clipboard!");
      }
    } catch (err) {
      console.log("Error sharing:", err);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const galleryImages = (p.other_images || [])
    .map((img) => (typeof img === "string" ? img : img?.image_url))
    .filter((img) => typeof img === "string" && img.length > 0);

  return (
    <div className="dp-page">
      <header className="dp-header">
        <button
          className="dp-back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <FiArrowLeftCircle size={30} />
        </button>
        <span className="dp-header-title">Details</span>
        <div className="dp-header-actions">
          <button
            className="dp-icon-btn"
            onClick={handleShare}
            title="Share Profile"
          >
            <FiShare2 size={18} />
          </button>
        </div>
      </header>

      {/* Hero */}
      <div className="dp-hero">
        <div
          className="dp-avatar-outer"
          onClick={() => {
            setIsAvatarLightbox(true);
            setActiveIdx(0);
            setLightboxOpen(true);
          }}
        >
          <div className="dp-avatar-wrapper">
            <img src={p.photo_url} alt={p.name} />
          </div>
          <div className="dp-avatar-zoom">
            <MdZoomOutMap size={14} />
          </div>
        </div>
        <div className="dp-hero-name">{p.name}</div>
        <div className="dp-hero-meta">
          <span>{p.gender}</span>
          <span>•</span>
          <span>{getAgeFromDob(p.dob)} yrs</span>
          <span>•</span>
          <span>{p.dob}</span>
        </div>
        <div className="dp-hero-tags">
          <span className="dp-hero-tag">
            <FaBriefcase size={12} /> {p.job}
          </span>
          <span className="dp-hero-tag">
            <IoLocationSharp size={14} />
            {p.city?.name}, {p.state?.name}
          </span>
        </div>
      </div>

      <div className="dp-content">
        {/* ── PHOTO GALLERY ── */}
        {galleryImages.length > 0 && (
          <SectionCard
            iconClass="orange"
            icon={<IoImages size={20} />}
            title="Photo Gallery"
          >
            <div className="dp-gallery-container">
              <div className="dp-swiper-outer">
                <div className="dp-swiper-zoom-badge">
                  <MdOutlineZoomOutMap size={14} />
                  <span>Tap to zoom</span>
                </div>
                <div className="dp-swiper-count-badge">
                  {activeIdx + 1} / {galleryImages.length}
                </div>

                <Swiper
                  onSwiper={setSwiperRef}
                  onSlideChange={(swiper) => setActiveIdx(swiper.activeIndex)}
                  onTap={(swiper) => {
                    setIsAvatarLightbox(false);
                    openLightbox(swiper.activeIndex);
                  }}
                  spaceBetween={0}
                  slidesPerView={1}
                  resistance={true}
                  resistanceRatio={0}
                  observer={true}
                  observeParents={true}
                  style={{ height: "100%" }}
                >
                  {galleryImages.map((img, idx) => (
                    <SwiperSlide key={idx}>
                      <img
                        src={img}
                        alt={`${p.name} view ${idx + 1}`}
                        className="dp-swiper-img"
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
                {galleryImages.length > 1 && (
                  <div className="dp-slider-dots">
                    {galleryImages.map((_, i) => (
                      <span
                        key={i}
                        className={`dp-dot ${activeIdx === i ? "active" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          swiperRef?.slideTo(i);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </SectionCard>
        )}

        {/* ── PERSONAL INFORMATION ── */}
        <SectionCard
          iconClass="blue"
          icon={<IoPerson size={16} />}
          title="Personal Information"
        >
          <div className="dp-section-body">
            <InfoRow
              icon={<IoCalendarOutline size={15} color="#6366f1" />}
              label="Date of Birth"
              value={p.dob || "N/A"}
            />
            <InfoRow
              icon={<TfiLocationPin size={15} color="#6366f1" />}
              label="Birth Place"
              value={p.place_of_birth}
            />
            <InfoRow
              icon={<CiClock2 size={15} color="#6366f1" />}
              label="Birth Time"
              value={p.time_of_birth}
            />
            <InfoRow
              icon={<MdOutlineHeight size={15} color="#6366f1" />}
              label="Height"
              value={p.height}
            />
            <InfoRow
              icon={<MdFitnessCenter size={15} color="#6366f1" />}
              label="Weight"
              value={p.weight}
            />
            <InfoRow
              icon={<MdFavorite size={15} color="#6366f1" />}
              label="Diet"
              value={p.diet}
            />
            <InfoRow
              icon={<FaMoon size={15} color="#6366f1" />}
              label="Religion"
              value={p.religion?.name}
            />
            <InfoRow
              icon={<IoHomeOutline size={15} color="#6366f1" />}
              label="Native"
              value={p.native}
            />
          </div>
        </SectionCard>

        {/* ── EDUCATION & CAREER ── */}
        <SectionCard
          iconClass="pink"
          icon={<FaGraduationCap size={16} />}
          title="Education & Career"
        >
          <div className="dp-section-body">
            <InfoRow
              icon={<IoSchoolOutline size={15} color="#ec4899" />}
              label="Education"
              value={p.education}
            />
            <InfoRow
              icon={<IoBriefcaseOutline size={15} color="#ec4899" />}
              label="Current Position"
              value={p.job}
            />
            <InfoRow
              icon={<IoBriefcaseOutline size={15} color="#ec4899" />}
              label="Profession"
              value={p.profession}
            />
            <InfoRow
              icon={<IoCashOutline size={15} color="#ec4899" />}
              label="Income"
              value={
                <div>
                  <div>
                    {p.income_monthly && p.income_monthly !== "null"
                      ? p.income_monthly
                      : ""}
                    <span>/month</span>
                  </div>
                  <div style={{ color: "#888" }}>
                    {p.income_yearly && p.income_yearly !== "null"
                      ? p.income_yearly
                      : ""}
                    <span>/year</span>
                  </div>
                </div>
              }
            />
            <InfoRow
              icon={<HiOutlineBuildingOffice2 size={15} color="#ec4899" />}
              label="Office Location"
              value={p.office_address || "N/A"}
            />
          </div>
        </SectionCard>

        {/* ── HOBBIES ── */}
        {p.hobby && p.hobby.trim() !== "" && (
          <SectionCard
            iconClass="orange"
            icon={<MdFavorite size={16} />}
            title="Hobbies & Interests"
          >
            <div className="dp-hobbies-list">
              {p.hobby.split(",").map((h, i) => (
                <span key={i} className="dp-hobby-tag">
                  {h}
                </span>
              ))}
            </div>
          </SectionCard>
        )}

        {/* ── FAMILY DETAILS ── */}
        <SectionCard
          iconClass="green"
          icon={<FaUsers size={16} />}
          title="Family Details"
        >
          {/* Father */}
          <div className="dp-fam-card">
            <div className="dp-fam-row">
              <span className="dp-fam-icon">
                <IoMale size={14} color="#10b981" />
              </span>
              <div>
                <div className="dp-fam-role">Father</div>
                <div className="dp-fam-name">{p.father_name}</div>
                <div className="dp-fam-sub">{p.father_occupation}</div>
              </div>
            </div>
            <div className="dp-fam-divider" />
            <div className="dp-fam-row">
              <span className="dp-fam-icon">
                <FiPhone size={12} color="#10b981" />
              </span>
              <div>
                <div className="dp-fam-role">Contact</div>
                <div className="dp-fam-name">
                  {p.father_contact_no || "N/A"}
                </div>
              </div>
            </div>
            <div className="dp-fam-divider" />
            <div className="dp-fam-row">
              <span className="dp-fam-icon">
                <IoIosHeartEmpty size={14} color="#10b981" />
              </span>
              <div>
                <div className="dp-fam-role">Father Living Together</div>
                {p.father_living === 1 ? (
                  <span className="dp-status-pill dp-pill-living">
                    ● Living
                  </span>
                ) : p.father_living === 0 ? (
                  <span className="dp-status-pill dp-pill-notliving">
                    ● Not Living
                  </span>
                ) : (
                  <span className="dp-na">N/A</span>
                )}
              </div>
            </div>
            <div className="dp-fam-divider" />
            <div className="dp-fam-row">
              <span className="dp-fam-icon">
                <FiUser size={14} color="#10b981" />
              </span>
              <div>
                <div className="dp-fam-role">Father's Status</div>
                {p.father_status === "alive" ? (
                  <span className="dp-status-pill dp-pill-living">● Alive</span>
                ) : p.father_status === "late" ? (
                  <span className="dp-status-pill dp-pill-notliving">
                    ● Late
                  </span>
                ) : (
                  <span className="dp-na">N/A</span>
                )}
              </div>
            </div>
          </div>

          {/* Mother */}
          <div className="dp-fam-card">
            <div className="dp-fam-row">
              <span className="dp-fam-icon">
                <IoFemale size={14} color="#f59e0b" />
              </span>
              <div>
                <div className="dp-fam-role">Mother</div>
                <div className="dp-fam-name">{p.mother_name}</div>
                <div className="dp-fam-sub">{p.mother_occupation}</div>
              </div>
            </div>
            <div className="dp-fam-divider" />
            <div className="dp-fam-row">
              <span className="dp-fam-icon">
                <FiPhone size={12} color="#f59e0b" />
              </span>
              <div>
                <div className="dp-fam-role">Contact</div>
                <div className="dp-fam-name">
                  {p.mother_contact_no || "N/A"}
                </div>
              </div>
            </div>
            <div className="dp-fam-divider" />
            <div className="dp-fam-row">
              <span className="dp-fam-icon">
                <IoIosHeartEmpty size={14} color="#f59e0b" />
              </span>
              <div>
                <div className="dp-fam-role">Status</div>
                {p.mother_living === 1 ? (
                  <span className="dp-status-pill dp-pill-living">
                    ● Living
                  </span>
                ) : p.mother_living === 0 ? (
                  <span className="dp-status-pill dp-pill-notliving">
                    ● Not Living
                  </span>
                ) : (
                  <span className="dp-na">N/A</span>
                )}
              </div>
            </div>
            <div className="dp-fam-divider" />
            <div className="dp-fam-row">
              <span className="dp-fam-icon">
                <FiUser size={14} color="#f59e0b" />
              </span>
              <div>
                <div className="dp-fam-role">Mother's Status</div>
                {p.mother_status === "alive" ? (
                  <span className="dp-status-pill dp-pill-living">● Alive</span>
                ) : p.mother_status === "late" ? (
                  <span className="dp-status-pill dp-pill-notliving">
                    ● Late
                  </span>
                ) : (
                  <span className="dp-na">N/A</span>
                )}
              </div>
            </div>
          </div>

          {/* Brothers */}
          {p.brothers && p.brothers.length > 0 && (
            <div className="dp-fam-section-label">Brothers</div>
          )}
          {(p.brothers || []).map((b, i) => (
            <div className="dp-fam-card" key={i}>
              <div className="dp-fam-row">
                <div className="dp-fam-avatar dp-fam-avatar--blue">
                  <IoPeopleOutline size={22} color="#0284c7" />
                </div>
                <div>
                  <div
                    className="dp-fam-name"
                    style={{ fontSize: 17, marginBottom: 6 }}
                  >
                    {b.brother_name}
                  </div>
                  <span className="dp-fam-type-pill dp-fam-type-pill--blue">
                    {b.brother_type || "N/A"}
                  </span>
                </div>
              </div>
              <div className="dp-fam-divider" />
              <div
                className="dp-fam-row"
                style={{
                  flexDirection: "column",
                  alignItems: "flex-start",
                  padding: "14px 16px",
                  gap: 4,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 4,
                  }}
                >
                  <IoBriefcaseOutline size={20} color="#0284c7" />
                  <span
                    className="dp-fam-role"
                    style={{ fontSize: 14, color: "#94a3b8" }}
                  >
                    Occupation
                  </span>
                </div>
                <div
                  className="dp-fam-name"
                  style={{ fontSize: 16, paddingLeft: 30 }}
                >
                  {b.brother_occupation || "N/A"}
                </div>
              </div>
            </div>
          ))}
          {/* Sisters */}
          {p.sisters && p.sisters.length > 0 && (
            <div className="dp-fam-section-label">Sisters</div>
          )}
          {(p.sisters || []).map((s, i) => (
            <div className="dp-fam-card" key={i}>
              <div
                className="dp-fam-row"
                style={{ alignItems: "center", padding: "16px 14px" }}
              >
                <div className="dp-fam-avatar dp-fam-avatar--pink">
                  <IoFemale size={22} color="#ec4899" />
                </div>
                <div>
                  <div
                    className="dp-fam-name"
                    style={{ fontSize: 17, marginBottom: 6 }}
                  >
                    {s.sister_name}
                  </div>
                  <span className="dp-fam-type-pill dp-fam-type-pill--pink">
                    {s.sister_type || "N/A"}
                  </span>
                </div>
              </div>

              <div className="dp-fam-divider" />
              <div
                className="dp-fam-row"
                style={{
                  flexDirection: "column",
                  alignItems: "flex-start",
                  padding: "14px 16px",
                  gap: 4,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 4,
                  }}
                >
                  <IoBriefcaseOutline size={20} color="#ec4899" />
                  <span
                    className="dp-fam-role"
                    style={{ fontSize: 14, color: "#94a3b8" }}
                  >
                    Occupation
                  </span>
                </div>
                <div
                  className="dp-fam-name"
                  style={{ fontSize: 16, paddingLeft: 30 }}
                >
                  {s.sister_occupation || "N/A"}
                </div>
              </div>

              <div className="dp-fam-divider" />
              <div
                className="dp-fam-row"
                style={{
                  flexDirection: "column",
                  alignItems: "flex-start",
                  padding: "14px 16px",
                  gap: 4,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 4,
                  }}
                >
                  <IoHeartOutline size={20} color="#ec4899" />
                  <span
                    className="dp-fam-role"
                    style={{ fontSize: 14, color: "#94a3b8" }}
                  >
                    Marital Status
                  </span>
                </div>
                <div style={{ paddingLeft: 30 }}>
                  {s.sister_marital_status ? (
                    <span
                      className={`dp-marital-pill dp-marital-${s.sister_marital_status || ""}`}
                    >
                      <span className="dp-marital-dot" />
                      {s.sister_marital_status}
                    </span>
                  ) : (
                    <span className="dp-na">N/A</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </SectionCard>

        {/* ── MATERNAL DETAILS ── */}
        <SectionCard
          iconClass="purple"
          icon={<FaHome size={16} />}
          title="Maternal Details"
        >
          <div className="dp-section-body">
            <InfoRow
              icon={<FiUser size={15} color="#8b5cf6" />}
              label="Name"
              value={p.mosal_name}
            />
            <InfoRow
              icon={<FiPhone size={13} color="#8b5cf6" />}
              label="Contact"
              value={p.mosal_number}
            />
            <InfoRow
              icon={<FiMapPin size={15} color="#8b5cf6" />}
              label="Location"
              value={
                <div>
                  <div className="dp-contact-value">
                    {p.mosalcity?.name} {p.mosalstate?.name}
                  </div>
                  <div className="dp-contact-address">{p.mosal_address}</div>
                </div>
              }
            />
          </div>
        </SectionCard>

        {/* ── REFERENCE ── */}
        <SectionCard
          iconClass="blue"
          icon={<FaUserTie size={15} />}
          title="Reference"
        >
          <div className="dp-ref-card">
            <div className="dp-ref-avatar">
              <IoMdContact size={28} color="#3b82f6" />
            </div>
            <div className="dp-ref-info">
              <div className="dp-ref-name">{p.reference_name}</div>
              <div className="dp-ref-detail">{p.reference_number}</div>
            </div>
          </div>
        </SectionCard>

        {/* ── CONTACT INFORMATION ── */}
        <SectionCard
          iconClass="red"
          icon={<MdEmail size={15} />}
          title="Contact Information"
        >
          <div className="dp-section-body">
            <div className="dp-contact-row">
              <div className="dp-contact-icon">
                <FiMail size={16} color="#ef4444" />
              </div>
              <div className="dp-contact-text">
                <div className="dp-contact-label">Email</div>
                <div className="dp-contact-value">{p.email}</div>
              </div>
            </div>
            <div className="dp-contact-row">
              <div className="dp-contact-icon">
                <FiMapPin size={16} color="#ef4444" />
              </div>
              <div className="dp-contact-text">
                <div className="dp-contact-label">Address</div>
                <div className="dp-contact-value">{p.address}</div>
                <div className="dp-contact-address">
                  {p.city?.name}, {p.state?.name}, {p.country?.name}
                </div>
              </div>
            </div>
          </div>
        </SectionCard>
      </div>

      {/* Lightbox */}
      <Lightbox
        open={lightboxOpen}
        close={closeLightbox}
        index={isAvatarLightbox ? 0 : activeIdx}
        slides={
          isAvatarLightbox
            ? [{ src: p.photo_url }]
            : galleryImages.map((img) => ({ src: img }))
        }
        plugins={[Zoom]}
        render={{
          buttonZoom: () => null,
          ...(isAvatarLightbox && {
            buttonPrev: () => null,
            buttonNext: () => null,
          }),
        }}
        carousel={isAvatarLightbox ? { finite: true } : undefined}
      />
    </div>
  );
};

export default DetailsPage;
